import { Customer, CustomerResponse, CustomerStatusLabel, ExpertisePointGroupTypeLabel } from 'types';
import React, { useRef, useState } from 'react';
import { useFormik } from 'formik';
import { customerResponseToCustomer, FormControlFormChasisNoInput, FormControlFormInput } from 'core';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  Center,
  SimpleGrid,
  useBoolean,
  Button,
  Select,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { Api } from 'api';
import { ExternalLinkIcon, SearchIcon } from '@chakra-ui/icons';
import { LoadingIndicator, VehicleBrandSelect, VehicleModelSelect, VehicleTypeSelect, DataTable } from 'components';
import { createColumnHelper } from '@tanstack/react-table';

interface SearchLicenseProps {
  isOpen: boolean;
  onClose: () => void;
  onSearchComplete: (customer: Customer) => void;
}

const columnHelper = createColumnHelper<Customer>();

export const SearchLicense: React.FC<SearchLicenseProps> = ({ isOpen, onClose, onSearchComplete }) => {
  const initialRef = useRef(null);
  const [isLoading, { on, off }] = useBoolean();
  const toast = useToast();
  const [searchedCustomers, setSearchedCustomers] = useState<Customer[]>([]);

  const { handleSubmit, handleChange, values, errors, resetForm } = useFormik({
    initialValues: {
      plate: '',
      chassisNumber: '',
      startDate: '',
      endDate: '',
      customerName: '',
      brandId: '',
      modelId: '',
      vehicleTypeId: '',
      adminStatusChanged: '',
      status: '',
      searchBrandId: undefined,
    },
    onSubmit: (data) => {
      if (data) {
        on();
        Api.Customer.searchCustomer2(data)
          .then((result) => {
            if (result.data.length) {
              const results = result.data;
              setSearchedCustomers(results);
            } else {
              setSearchedCustomers([]);
              toast({
                status: 'warning',
                title: 'Sonuç bulunamadı',
                isClosable: true,
              });
            }
          })
          .catch((err) => {
            console.error(err);
          })
          .then(() => off());
      }
    },
  });

  const closeModal = () => {
    resetForm();
    onClose();
  };

  const goToCustomerDetails = (selectedCustomer: Customer) => {
    const formatCustomer: CustomerResponse = {
      customer: selectedCustomer,
      canContinue: selectedCustomer.canContinue,
      displayPlate: selectedCustomer.plate,
      hasKvkkApproval: selectedCustomer.hasKvkkApproval,
      hasIsemriApproval: selectedCustomer.hasIsemriApproval,
    };
    onSearchComplete(customerResponseToCustomer(formatCustomer));
    toast({
      status: 'info',
      title: 'Veri arama sonuçları listesine eklendi',
      isClosable: true,
    });
    closeModal();
    // setSearchedCustomers([]);
    // closeModal();
  };

  const columns = [
    columnHelper.accessor('createdAt', {
      header: 'Tarih',
      cell: (info) => new Date(info.getValue()!).toLocaleDateString() || '-',
    }),
    columnHelper.accessor('expertisePackage', {
      header: 'Paket',
      cell: (info) => info.getValue()?.name || '-',
    }),
    columnHelper.accessor('plate', {
      header: 'Plaka',
    }),
    columnHelper.accessor('customerVehicle', {
      header: 'Marka',
      cell: (info) => info.getValue()?.brand?.name || '-',
      id: 'brand',
    }),
    columnHelper.accessor('customerVehicle', {
      header: 'Model',
      cell: (info) => info.getValue()?.model?.name || '-',
      id: 'model',
    }),
    columnHelper.accessor('customerVehicle', {
      header: 'Şase No',
      cell: (info) => info.getValue()?.chassisNumber || '-',
      id: 'chassisNumber',
    }),
    columnHelper.accessor('customerVehicle', {
      header: 'Tipi',
      cell: (info) => info.getValue()?.vehicleType?.name || '-',
      id: 'vehicleType',
    }),
    columnHelper.accessor('customerVehicle', {
      header: 'Yılı',
      cell: (info) => info.getValue()?.modelYear || '-',
      id: 'modelYear',
    }),
    columnHelper.accessor('name', {
      header: 'Müşteri',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('phoneNumber', {
      header: 'Telefon',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Aksiyon',
      cell: (info) => (
        <Button
          onClick={() => goToCustomerDetails(info.row.original)}
          leftIcon={<ExternalLinkIcon />}
          colorScheme="blue"
          variant="solid"
        >
          Detay
        </Button>
      ),
    }),
  ];

  return (
    <Modal
      onClose={closeModal}
      isOpen={isOpen}
      initialFocusRef={initialRef}
      closeOnOverlayClick={!isLoading}
      closeOnEsc={!isLoading}
      size="full"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Arama</ModalHeader>
        <ModalCloseButton disabled={isLoading} />
        <ModalBody>
          <form id="searchLicenseForm" noValidate onSubmit={handleSubmit}>
            <Center>
              <SimpleGrid spacing="30px" columns={[1, null, 2, 3, 3, 7]} justifyContent="center">
                <FormControlFormInput
                  label="Başlangıç Tarihi"
                  ref={initialRef}
                  type="date"
                  name="startDate"
                  onChange={handleChange}
                  value={values.startDate}
                  error={errors.startDate != null}
                  helperText={errors.startDate}
                  defaultValue={values.startDate}
                />
                <FormControlFormInput
                  label="Bitiş Tarihi"
                  ref={initialRef}
                  type="date"
                  name="endDate"
                  onChange={handleChange}
                  value={values.endDate}
                  error={errors.endDate != null}
                  helperText={errors.endDate}
                  defaultValue={values.endDate}
                />
                <FormControlFormInput
                  label="Plaka"
                  ref={initialRef}
                  placeholder="Plaka"
                  name="plate"
                  onChange={handleChange}
                  value={values.plate}
                  error={errors.plate != null}
                  helperText={errors.plate}
                />
                <FormControlFormChasisNoInput
                  label="Şase No"
                  name="chassisNumber"
                  onChange={handleChange}
                  value={values.chassisNumber}
                  error={errors.chassisNumber != null}
                  helperText={errors.chassisNumber}
                />
                <FormControlFormInput
                  label="Ad"
                  ref={initialRef}
                  placeholder="Müşteri"
                  name="customerName"
                  onChange={handleChange}
                  value={values.customerName}
                  error={errors.customerName != null}
                  helperText={errors.customerName}
                />
                <VehicleBrandSelect
                  name="brandId"
                  onChange={handleChange}
                  value={values.brandId}
                  error={errors.brandId != null}
                  helperText={errors.brandId}
                />
                <VehicleModelSelect
                  brandId={values.brandId}
                  name="modelId"
                  value={values.modelId}
                  onChange={handleChange}
                  error={errors.modelId != null}
                  helperText={errors.modelId}
                />
                <VehicleTypeSelect
                  name="vehicleTypeId"
                  onChange={handleChange}
                  value={values.vehicleTypeId}
                  error={errors.vehicleTypeId != null}
                  helperText={errors.vehicleTypeId}
                />

                <FormControl>
                  <FormLabel>Kilit Durumu</FormLabel>
                  <Select name="adminStatusChanged" value={values.adminStatusChanged} onChange={handleChange}>
                    <option value="">Tümü</option>
                    <option value="true">Kilidi Açık</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Ekspertiz Durumu</FormLabel>
                  <Select name="status" value={values.status} onChange={handleChange}>
                    <option value="">Tümü</option>
                    {Array.from(CustomerStatusLabel.entries()).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Marka</FormLabel>
                  <Select name="searchBrandId" value={values.searchBrandId} onChange={handleChange}>
                    <option value="">Tümü</option>
                    {Array.from(ExpertisePointGroupTypeLabel.entries()).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel> &nbsp; </FormLabel>
                  <Button
                    width="200px"
                    type="submit"
                    form="searchLicenseForm"
                    leftIcon={<SearchIcon />}
                    colorScheme="blue"
                    isLoading={isLoading}
                    isDisabled={isLoading}
                  >
                    Ara
                  </Button>
                </FormControl>
              </SimpleGrid>
            </Center>

            {searchedCustomers.length >= 1 && (
              <div style={{ marginTop: '40px' }}>
                <DataTable
                  data={searchedCustomers}
                  columns={columns}
                  usePagination
                  tableProps={{
                    variant: 'striped',
                    colorScheme: 'teal',
                  }}
                />
              </div>
            )}
          </form>
          {isLoading && <LoadingIndicator />}
        </ModalBody>
        <ModalFooter justifyContent="space-between">
          <Button onClick={closeModal} disabled={isLoading}>
            Kapat
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
